'use client';

import { useState, useRef, useEffect, useCallback } from 'react';
import useSWR from 'swr';
import Link from 'next/link';
import { apiFetch } from '../lib/api';
import { useTranslation } from '../lib/i18n';
import { useToast } from './Toast';
import { useExitAnimation } from '../lib/useExitAnimation';
import PhoneInput from './PhoneInput';

type AccountRow = {
  id: number;
  firma: string | null;
  jmeno: string | null;
  ico: string | null;
  obec: string | null;
  priceGroupId?: number | null;
  pohodaId?: string | null;
};

type ContactRow = {
  id: number;
  fullName: string | null;
  firstName: string | null;
  lastName: string | null;
  position: string | null;
  accountId?: number | null;
};

type PagedResponse<T> = {
  data: T[];
  total: number;
  page: number;
  size: number;
  totalPages: number;
};

const accountLabel = (a: AccountRow): string =>
  a.firma || a.jmeno || `#${a.id}`;
const contactLabel = (c: ContactRow): string =>
  c.fullName || [c.firstName, c.lastName].filter(Boolean).join(' ') || `#${c.id}`;

type Endpoints = {
  /** Template with `${search}` placeholder for account search. Defaults to admin paginated endpoint. */
  accountSearch?: (search: string) => string;
  /** Template with `${search}` placeholder for contact search. Defaults to admin paginated endpoint. */
  contactSearch?: (search: string) => string;
  /** URL for fetching contacts of a specific account. Defaults to admin endpoint. */
  accountContacts?: (accountId: number) => string;
};

type Props = {
  accountId: number | null;
  accountPohodaId?: string | null;
  accountName: string | null;
  contactId: number | null;
  contactName: string | null;
  onChange: (next: {
    accountId: number | null;
    accountName: string | null;
    accountPohodaId?: string | null;
    contactId: number | null;
    contactName: string | null;
    priceGroupId?: number | null;
  }) => void;
  disabled?: boolean;
  endpoints?: Endpoints;
  /** Compact layout (no labels, smaller spacing) — useful inside forms. */
  compact?: boolean;
  /** Render account and contact side by side (grid-cols-2) instead of stacked. */
  horizontal?: boolean;
  /** Override account label (default: t('calendarAccount') = "Firma") */
  accountLabelText?: string;
  /** Override contact label (default: t('calendarContact') = "Kontakt") */
  contactLabelText?: string;
  /** Optional hint text rendered under the contact field */
  contactHint?: string;
  /** Optional extra row rendered under the account field (e.g. email input) */
  accountExtra?: React.ReactNode;
};

export default function AccountContactPicker({
  accountId,
  accountPohodaId,
  accountName,
  contactId,
  contactName,
  onChange,
  disabled,
  endpoints,
  compact,
  horizontal,
  accountLabelText,
  contactLabelText,
  contactHint,
  accountExtra,
}: Props) {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const accLabel = accountLabelText ?? t('calendarAccount');
  const conLabel = contactLabelText ?? t('calendarContact');
  const [accountQuery, setAccountQuery] = useState('');
  const [accountOpen, setAccountOpen] = useState(false);
  const [contactQuery, setContactQuery] = useState('');
  const [contactOpen, setContactOpen] = useState(false);
  const accountBoxRef = useRef<HTMLDivElement>(null);
  const contactBoxRef = useRef<HTMLDivElement>(null);

  // Endpoint resolvers — use custom endpoints if provided, otherwise default to admin paginated API.
  const adminAccountSearch = (s: string) => `/api/admin/obchod/accounts?search=${encodeURIComponent(s)}&size=20`;
  const adminContactSearch = (s: string) => `/api/admin/obchod/contacts?search=${encodeURIComponent(s)}&size=20`;
  const adminAccountContacts = (aid: number) => `/api/admin/obchod/accounts/${aid}/contacts`;
  const accountSearchUrl = endpoints?.accountSearch || adminAccountSearch;
  const contactSearchUrlFn = endpoints?.contactSearch || adminContactSearch;
  const accountContactsUrlFn = endpoints?.accountContacts || adminAccountContacts;
  // When using user-level (analytics) endpoints, the response is a plain array, not paged.
  const accountIsPaged = !endpoints?.accountSearch;
  const contactIsPaged = !endpoints?.contactSearch;

  // Debounced account search
  const [accountSearch, setAccountSearch] = useState('');
  useEffect(() => {
    const handle = setTimeout(() => setAccountSearch(accountQuery.trim()), 250);
    return () => clearTimeout(handle);
  }, [accountQuery]);

  const { data: accountResultsPaged, isLoading: accountsLoading } = useSWR<PagedResponse<AccountRow>>(
    accountOpen && accountSearch.length > 0 && accountIsPaged
      ? accountSearchUrl(accountSearch)
      : null,
  );
  const { data: accountResultsFlat } = useSWR<AccountRow[]>(
    accountOpen && accountSearch.length > 0 && !accountIsPaged
      ? accountSearchUrl(accountSearch)
      : null,
  );
  const accountResults = accountIsPaged ? accountResultsPaged?.data : accountResultsFlat;

  // Contacts: when an account is selected, prefer fetching that account's contacts.
  // Otherwise, free-text search across all contacts.
  const [contactSearch, setContactSearch] = useState('');
  useEffect(() => {
    const handle = setTimeout(() => setContactSearch(contactQuery.trim()), 250);
    return () => clearTimeout(handle);
  }, [contactQuery]);

  const accountContactsUrl = accountId
    ? accountContactsUrlFn(accountId)
    : null;
  const contactSearchUrl = !accountId && contactOpen && contactSearch.length > 0
    ? contactSearchUrlFn(contactSearch)
    : null;

  const { data: accountContacts } = useSWR<ContactRow[]>(
    accountContactsUrl,
  );

  const { data: contactResultsPaged, isLoading: contactsLoading } = useSWR<PagedResponse<ContactRow>>(
    contactIsPaged ? contactSearchUrl : null,
  );
  const { data: contactResultsFlat } = useSWR<ContactRow[]>(
    !contactIsPaged ? contactSearchUrl : null,
  );
  const contactResults = contactIsPaged ? contactResultsPaged?.data : contactResultsFlat;

  // Close dropdowns on outside click
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (accountBoxRef.current && !accountBoxRef.current.contains(e.target as Node)) {
        setAccountOpen(false);
      }
      if (contactBoxRef.current && !contactBoxRef.current.contains(e.target as Node)) {
        setContactOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const pickAccount = useCallback((a: AccountRow) => {
    onChange({
      accountId: a.id,
      accountName: accountLabel(a),
      accountPohodaId: a.pohodaId ?? null,
      // Reset contact when account changes — the previous contact may not belong to this account.
      contactId: null,
      contactName: null,
      priceGroupId: a.priceGroupId ?? null,
    });
    setAccountQuery('');
    setAccountOpen(false);
    setContactQuery('');
  }, [onChange]);

  const clearAccount = useCallback(() => {
    onChange({ accountId: null, accountName: null, accountPohodaId: null, contactId: null, contactName: null, priceGroupId: null });
    setAccountQuery('');
    setContactQuery('');
  }, [onChange]);

  const pickContact = useCallback((c: ContactRow) => {
    onChange({
      accountId,
      accountName,
      accountPohodaId,
      contactId: c.id,
      contactName: contactLabel(c),
    });
    setContactQuery('');
    setContactOpen(false);
  }, [onChange, accountId, accountName, accountPohodaId]);

  const clearContact = useCallback(() => {
    onChange({ accountId, accountName, accountPohodaId, contactId: null, contactName: null });
    setContactQuery('');
  }, [onChange, accountId, accountName, accountPohodaId]);

  // Quick-create contact modal
  const [showCreateContact, setShowCreateContact] = useState(false);
  const createContactExiting = useExitAnimation(showCreateContact);
  const [newContact, setNewContact] = useState({ firstName: '', lastName: '', email: '', phone: '' });
  const [creatingContact, setCreatingContact] = useState(false);

  const handleCreateContact = async () => {
    if (!newContact.firstName.trim() && !newContact.lastName.trim() && !newContact.email.trim()) return;
    setCreatingContact(true);
    try {
      const created = await apiFetch<{ id: number; fullName: string | null; firstName: string | null; lastName: string | null }>('/api/admin/obchod/contacts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...newContact,
          accountId: accountId || 0,
          source: 'RUCNE',
        }),
      });
      if (created) {
        onChange({
          accountId,
          accountName,
          contactId: created.id,
          contactName: created.fullName || [created.firstName, created.lastName].filter(Boolean).join(' ') || `#${created.id}`,
        });
        setShowCreateContact(false);
        setNewContact({ firstName: '', lastName: '', email: '', phone: '' });
        setContactQuery('');
        setContactOpen(false);
        showToast(t('toastCreated'), 'success');
      }
    } catch {
      // error handled by apiFetch
    } finally {
      setCreatingContact(false);
    }
  };

  const contactOptions: ContactRow[] = accountId
    ? (accountContacts || [])
    : (contactResults || []);

  return (
    <div className={horizontal ? 'grid grid-cols-2 gap-3' : 'space-y-2'}>
      {/* Account */}
      <div>        {!compact && <label className="block text-sm font-medium text-wood mb-1">{accLabel}</label>}
        {accountId ? (
          <div className="flex items-center justify-between rounded-rustic border border-wheat bg-cream px-3 py-2 text-sm text-bark">
            <div className="flex items-center gap-1.5 min-w-0">
              <Link href={`/obchod/accounts/${accountPohodaId ?? accountId}`} className="truncate text-moss-dark hover:underline">{accountName || `#${accountId}`}</Link>
              <button
                type="button"
                onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/obchod/accounts/${accountPohodaId ?? accountId}`); showToast(t('linkCopied'), 'success'); }}
                className="text-wood hover:text-moss transition flex-shrink-0"
                title={t('copyLink')}
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>
              </button>
              <a
                href={`/obchod/accounts/${accountPohodaId ?? accountId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-wood hover:text-moss transition flex-shrink-0"
                title={t('openInNewTab')}
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
              </a>
            </div>
            {!disabled && (
              <button type="button" onClick={clearAccount}
                className="text-xs text-wood hover:text-rust transition ml-2">×</button>
            )}
          </div>
        ) : (
          <div ref={accountBoxRef} className="relative">
            <input
              type="text"
              value={accountQuery}
              disabled={disabled}
              onFocus={() => setAccountOpen(true)}
              onChange={e => { setAccountQuery(e.target.value); setAccountOpen(true); }}
              placeholder={compact ? t('calendarAccountSearch') : t('calendarAccountSearch')}
              className="w-full rounded-rustic border border-wheat bg-cream px-3 py-2 text-sm text-bark focus:outline-none focus:ring-1 focus:ring-moss disabled:opacity-50"
            />
            {accountOpen && accountSearch.length > 0 && (
              <div className="absolute z-20 mt-1 w-full max-h-48 overflow-y-auto rounded-rustic border border-wheat bg-cream shadow-lg">
                {accountsLoading && <div className="px-3 py-2 text-xs text-wood">…</div>}
                {!accountsLoading && accountResults?.length === 0 && (
                  <div className="px-3 py-2 text-xs text-wood">{t('calendarNoAccount')}</div>
                )}
                {accountResults?.map(a => (
                  <button key={a.id} type="button" onClick={() => pickAccount(a)}
                    className="block w-full text-left px-3 py-2 text-sm text-bark hover:bg-linen transition">
                    <span className="font-medium">{accountLabel(a)}</span>
                    {a.ico && <span className="text-xs text-wood ml-2">{t('accIcoColon')} {a.ico}</span>}
                    {a.obec && <span className="text-xs text-wood ml-2">{a.obec}</span>}
                  </button>
                ))}
                {/* Quick link to accounts page */}
                <div className="border-t border-wheat/30 mt-1 pt-1">
                  <Link href="/obchod/accounts" target="_blank"
                    className="block w-full text-left px-3 py-2 text-xs text-moss-dark hover:bg-linen transition font-medium">
                    + {t('caseBrowseAccounts')}
                  </Link>
                </div>
              </div>
            )}
          </div>
        )}
        {accountExtra && (
          <div className="mt-1">{accountExtra}</div>
        )}
      </div>

      {/* Contact */}
      <div>
        {!compact && <label className="block text-sm font-medium text-wood mb-1">{conLabel}</label>}
        {contactId ? (
          <div className="flex items-center justify-between rounded-rustic border border-wheat bg-cream px-3 py-2 text-sm text-bark">
            <div className="flex items-center gap-1.5 min-w-0">
              <Link href={`/contacts?contactId=${contactId}&accountId=${accountId}`} className="truncate text-moss-dark hover:underline">{contactName || `#${contactId}`}</Link>
              <button
                type="button"
                onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/contacts?contactId=${contactId}&accountId=${accountId}`); showToast(t('linkCopied'), 'success'); }}
                className="text-wood hover:text-moss transition flex-shrink-0"
                title={t('copyLink')}
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>
              </button>
              <a
                href={`/contacts?contactId=${contactId}&accountId=${accountId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-wood hover:text-moss transition flex-shrink-0"
                title={t('openInNewTab')}
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
              </a>
            </div>
            {!disabled && (
              <button type="button" onClick={clearContact}
                className="text-xs text-wood hover:text-rust transition ml-2">×</button>
            )}
          </div>
        ) : (
          <div ref={contactBoxRef} className="relative">
            <input
              type="text"
              value={contactQuery}
              disabled={disabled}
              onFocus={() => setContactOpen(true)}
              onChange={e => { setContactQuery(e.target.value); setContactOpen(true); }}
              placeholder={accountId ? t('calendarContactsForAccount') : t('calendarContactSearch')}
              className="w-full rounded-rustic border border-wheat bg-cream px-3 py-2 text-sm text-bark focus:outline-none focus:ring-1 focus:ring-moss disabled:opacity-50"
            />
            {contactOpen && (accountId || contactSearch.length > 0) && (
              <div className="absolute z-20 mt-1 w-full max-h-48 overflow-y-auto rounded-rustic border border-wheat bg-cream shadow-lg">
                {accountId && !accountContacts && <div className="px-3 py-2 text-xs text-wood">…</div>}
                {!accountId && contactsLoading && <div className="px-3 py-2 text-xs text-wood">…</div>}
                {!accountId && !contactsLoading && contactResults?.length === 0 && (
                  <div className="px-3 py-2 text-xs text-wood">{t('calendarNoContact')}</div>
                )}
                {contactOptions.length === 0 && accountId && accountContacts && (
                  <div className="px-3 py-2 text-xs text-wood">{t('calendarNoContact')}</div>
                )}
                {contactOptions.map(c => (
                  <button key={c.id} type="button" onClick={() => pickContact(c)}
                    className="block w-full text-left px-3 py-2 text-sm text-bark hover:bg-linen transition">
                    <span className="font-medium">{contactLabel(c)}</span>
                    {c.position && <span className="text-xs text-wood ml-2">{c.position}</span>}
                  </button>
                ))}
                {/* Quick-create new contact */}
                <div className="border-t border-wheat/30 mt-1 pt-1">
                  <button type="button" onClick={() => setShowCreateContact(true)}
                    className="block w-full text-left px-3 py-2 text-xs text-moss-dark hover:bg-linen transition font-medium">
                    + {t('caseCreateNewContact')}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
        {contactHint && (
          <div className="text-xs text-wood pl-1 mt-1">{contactHint}</div>
        )}
      </div>

      {/* Quick-create contact modal */}
      {(showCreateContact || createContactExiting) && (
        <div className={`fixed inset-0 z-50 flex items-center justify-center bg-bark/40 backdrop-blur-sm ${showCreateContact ? 'animate-backdropIn' : 'animate-backdropOut'}`} onClick={() => setShowCreateContact(false)}>
          <div role="dialog" aria-modal="true" className={`bg-cream rounded-rustic shadow-2xl border border-wheat p-6 w-full max-w-md ${showCreateContact ? 'animate-modalIn' : 'animate-modalOut'}`} onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-bark mb-4">+ {t('caseCreateNewContact')}</h3>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-wood font-medium block mb-1">{t('caseContactFirstName')}</label>
                  <input
                    type="text"
                    value={newContact.firstName}
                    onChange={e => setNewContact({ ...newContact, firstName: e.target.value })}
                    className="w-full rounded-rustic border border-wheat bg-cream px-3 py-2 text-sm text-bark focus:outline-none focus:ring-1 focus:ring-moss"
                    autoFocus
                  />
                </div>
                <div>
                  <label className="text-xs text-wood font-medium block mb-1">{t('caseContactLastName')}</label>
                  <input
                    type="text"
                    value={newContact.lastName}
                    onChange={e => setNewContact({ ...newContact, lastName: e.target.value })}
                    className="w-full rounded-rustic border border-wheat bg-cream px-3 py-2 text-sm text-bark focus:outline-none focus:ring-1 focus:ring-moss"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs text-wood font-medium block mb-1">{t('caseCustomerEmail')}</label>
                <input
                  type="email"
                  value={newContact.email}
                  onChange={e => setNewContact({ ...newContact, email: e.target.value })}
                  className="w-full rounded-rustic border border-wheat bg-cream px-3 py-2 text-sm text-bark focus:outline-none focus:ring-1 focus:ring-moss"
                />
              </div>
              <div>
                <label className="text-xs text-wood font-medium block mb-1">{t('caseContactPhone')}</label>
                <PhoneInput
                  value={newContact.phone}
                  onChange={v => setNewContact({ ...newContact, phone: v })}
                  showLabel={false}
                  className="w-full rounded-rustic border border-wheat bg-cream px-3 py-2 text-sm text-bark focus:outline-none focus:ring-1 focus:ring-moss"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button type="button" onClick={() => setShowCreateContact(false)}
                className="px-4 py-2 text-sm rounded-rustic border border-wheat text-wood hover:bg-wheat/30 transition">
                {t('cancel')}
              </button>
              <button type="button" onClick={handleCreateContact} disabled={creatingContact}
                className="px-4 py-2 text-sm rounded-rustic bg-moss text-cream hover:bg-moss-dark transition font-medium disabled:opacity-50">
                {creatingContact ? '…' : t('save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
